import { Button, Collapse, Stack, TableCell, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { setPageName } from '../../store/redux/reducers/pageNameSlice'
import { useAppDispatch } from '../../store/redux/hooks'
import { Column, Row } from '../../store/types/utils/DataTableUtils'
import { useGetDictionariesQuery } from '../../store/redux/reducers/dictionarySlice'
import defaultProps from '../../config/defaultProps'
import { StickyCell } from '../../styles/WorklogTableStyles'
import DataTable from '../../components/Table/DataTable'
import { Add, FilterList } from '@mui/icons-material'
import { Box } from '@mui/system'
import FilterDictionariesMenu from '../../components/FilterMenu/FilterDictionariesMenu'
import DictionariesActionMenu from '../../components/Popup/DictionariesActionMenu'
import Searchbar from '../../components/Searchbar/Searchbar'
import AddEditDictionaryDialog from './AddEditDictionaryDialog'
import Dictionary from '../../store/types/models/Dictionary'
import LocalStorageTypes from '../../store/types/enums/LocalStorageTypes'
import SessionStorageKeys from '../../store/types/enums/SessionStorageKeys'
import { pageHeaderActions } from '../../store/redux/reducers/pageHeaderSlice'
import usePersistedPage from '../../store/utils/usePersistedPage'
import { breadcrumbsItemsActions } from '../../store/redux/reducers/breadcrumbsSlice'

const DictionarySettingsView = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const theme = useTheme()
  useEffect(() => {
    dispatch(setPageName(t('page.name.dictionarySettings')))
    dispatch(pageHeaderActions.setInitialState())
    dispatch(breadcrumbsItemsActions.setInitialState())
    dispatch(
      breadcrumbsItemsActions.setSelectedItems({
        adminSettings: `${t('page.name.adminSettings.dictionary')}`,
      })
    )
  }, [dispatch, t])
  const [page, setPage] = usePersistedPage(SessionStorageKeys.DictionariesTablePage)
  const [requestProps, setRequestProps] = useState({ ...defaultProps, page: page })
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'))
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const [open, setOpen] = useState(false)

  const { data: dictionariesResponse } = useGetDictionariesQuery(requestProps)

  const columns: Column[] = [
    { id: 'group', label: t('page.dictionarySettings.table.header.group') },
    { id: 'value', label: t('page.dictionarySettings.table.header.value') },
    { id: 'key', label: t('page.dictionarySettings.table.header.key') },
    { id: 'action', label: t('components.table.header.action') },
  ]

  const renderDictionaryTile = (dictionary: Row, labelId: string) => {
    const castedDictionary = dictionary as Dictionary

    return (
      <>
        <Stack direction="row" justifyContent="space-between">
          <Typography
            variant="h6"
            color="textSecondary"
            sx={{ flex: 1, whiteSpace: 'normal', wordBreak: 'break-word' }}
          >
            {castedDictionary.group}
          </Typography>
          <DictionariesActionMenu dictionary={castedDictionary} />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center"></Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight="bold" color="textSecondary">
            {t('page.dictionarySettings.table.header.value')}:
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {castedDictionary.value}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight="bold" color="textSecondary">
            {t('page.dictionarySettings.table.header.key')}:
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {castedDictionary.key ? castedDictionary.key : '-'}
          </Typography>
        </Stack>
      </>
    )
  }

  const renderRow = (dictionary: Row) => {
    const castedDictionary = dictionary as Dictionary
    return (
      <>
        <TableCell sx={{ wordWrap: 'break-word', maxWidth: 200 }}>{castedDictionary.group}</TableCell>
        <TableCell>{castedDictionary.value}</TableCell>
        <TableCell>{castedDictionary.key ? castedDictionary.key : '-'}</TableCell>
        <StickyCell
          align="center"
          sx={{
            borderLeft: '2px solid',
            borderLeftColor: 'background.elevation8',
            right: 0,
            '&:hover': {
              cursor: 'auto',
            },
          }}
        >
          <DictionariesActionMenu dictionary={castedDictionary} />
        </StickyCell>
      </>
    )
  }

  const handleSearch = (value: string) => {
    setRequestProps({ ...requestProps, 'query:value,key,group': value })
    setPage(0)
  }

  const handleAddClick = () => {
    setOpen(true)
  }

  return (
    <Stack sx={{ gap: 2 }}>
      <Stack direction={isDownSm ? 'column-reverse' : 'row'} sx={{ justifyContent: 'space-between', gap: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          disableElevation
          startIcon={<FilterList />}
          size="medium"
          className={isFilterMenuOpen ? 'selected' : ''}
          onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
          fullWidth={isDownSm}
        >
          {t('page.usersProfile.filterTags')}
        </Button>
        <Searchbar placeholder={t('page.dictionarySettings.searchbar')} handleChange={handleSearch} />
        <Button variant="contained" color="primary" disableElevation size="medium" onClick={handleAddClick}>
          <Add />
          {t('page.dictionarySettings.buttons.addDictionary')}
        </Button>
        <AddEditDictionaryDialog open={open} setOpen={setOpen} />
      </Stack>
      <Box
        display={'flex'}
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent={'space-between'}
        gap={isFilterMenuOpen ? 2 : 0}
        height="fit-content"
        pb={3}
      >
        {isDownMd && (
          <Collapse in={isFilterMenuOpen} orientation="vertical">
            <FilterDictionariesMenu requestProps={requestProps} setRequestProps={setRequestProps} setPage={setPage} />
          </Collapse>
        )}
        {!isDownMd && (
          <Collapse in={isFilterMenuOpen} orientation="horizontal">
            <FilterDictionariesMenu requestProps={requestProps} setRequestProps={setRequestProps} setPage={setPage} />
          </Collapse>
        )}
        <DataTable
          unsortableColumns={[{ id: 'action', label: t('components.table.header.action') }]}
          renderTileContent={(dictionary: Row, labelId: string) => renderDictionaryTile(dictionary, labelId)}
          renderTileCheckbox={false}
          singleSelection={false}
          columns={columns}
          data={dictionariesResponse?.content || []}
          renderRow={renderRow}
          totalElements={dictionariesResponse?.totalElements || 0}
          setRequestProps={setRequestProps}
          translationForNotFound={t('page.dictionarySettings.table.notFound')}
          page={page}
          setPage={setPage}
          removeCheckboxes
          hoverOff
          commonRowsPerPage={
            localStorage.getItem(LocalStorageTypes.DictionariesRows)
              ? parseInt(localStorage.getItem(LocalStorageTypes.DictionariesRows)!)
              : undefined
          }
          localStorageRowsName={LocalStorageTypes.DictionariesRows}
        />
      </Box>
    </Stack>
  )
}

export default DictionarySettingsView
